import React, { useEffect, useState } from 'react';
import "./BDT_battery_Bus.css"
import useHttp from '../../../hook/use-http';
import { API_URL_MAIN } from '../../../constants/driveglobalfilterinitalstates_bus';
import Tooltip from 'react-bootstrap/Tooltip';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import ColorLegend from "./BDT_ColorLegend_Bus"
import ChartLoader from '../../Shared/Loader';
import moment from 'moment';
import { getCellEvent, getTemperatureVoltageByCell } from '../../../services/cesl-bus/batteryDigitalTwinServices';

const BDT_Battery = (props) => {

    const { filterdata, packType } = props
    const { isLoading, error, sendReq: fetchData } = useHttp();
    const [cellData, setCellData] = useState({})
    const [overAllData, setOverAllData] = useState({})
    const [batteryFilterType, setBatteryFilterType] = useState("Cell_Out_Temp_Max")
    const [selectedCell, setSelectedCell] = useState({ temperature: 0, cellNum: 0, })
    const [loaderForCells, setLoaderForCells] = useState(false)
    const [commentDetails, setCommentDetails] = useState({ value: null, date: null })

    // Static data for 1 to 104 cell numbers with predefined temperatures
    const numbersWithTemp = Array.from({ length: 104 }, (_, i) => ({ cellNumber: i + 1 }));

    useEffect(() => {
        setLoaderForCells(true)
        if (batteryFilterType) {
            fetchData(
                {
                    url: API_URL_MAIN + `battery_digital_twin/${batteryFilterType}`,
                    body: { filterdata, packType },
                },
                (data) => {
                    if (data?.hits?.length) {
                        setCellData(data.hits[0]?._source)
                        setSelectedCell({ temperature: 0, cellNum: 0 })
                        setLoaderForCells(false)
                    }
                    else {
                        setCellData({})
                        setLoaderForCells(false)
                    }
                }
            );
        }
        if (batteryFilterType === "overall_cell_data") {
            fetchData(
                {
                    url: API_URL_MAIN + `battery_digital_twin/overall_cell_data`,
                    body: { filterdata, packType },
                },
                (data) => {
                    if (data?.hits?.length) {
                        setOverAllData(data.hits[0]?._source)
                        setSelectedCell({ temperature: 0, cellNum: 0 })
                        setLoaderForCells(false)
                    }
                    else {
                        setOverAllData({})
                        setLoaderForCells(false)
                    }
                }
            );
        }

    }, [batteryFilterType, props])

    const rows = 26;
    const cols = 4;

    // Split the numbers into 4 columns (each column will have 23 cells)
    const columns = Array.from({ length: cols }, (_, colIndex) => {
        const columnData = numbersWithTemp.slice(colIndex * rows, (colIndex + 1) * rows);

        if (colIndex % 2 === 0) {
            return columnData.reverse();
        }
        return columnData;
    });

    let values = []

    if (batteryFilterType == "overall_cell_data") {
        const tempData = Object.fromEntries(
            Object.entries(cellData).filter(([key, value]) => key.includes("Temp_Last_Point"))
        );
        values = Object.values(tempData);
    } else {
        values = Object.values(cellData);
    }

    let minVal = batteryFilterType.includes("Thermal_runaway") ? 0 : batteryFilterType.includes("Cell_Out_Volt") ? 2 : batteryFilterType.includes("Cell_Out_Temp") ? -20 : Math.min(...values).toFixed(4)
    let maxVal = batteryFilterType.includes("Thermal_runaway") ? 2 : batteryFilterType.includes("Cell_Out_Volt") ? 4 : batteryFilterType.includes("Cell_Out_Temp") ? 50 : Math.max(...values).toFixed(4)

    function interpolateColorBasedOnValue(value) {
        const minValue = minVal//batteryFilterType.includes("Thermal_runaway") ? 0 : parseFloat(minVal?.toFixed(4));
        const maxValue = maxVal//batteryFilterType.includes("Thermal_runaway") ? 2 : parseFloat(maxVal?.toFixed(4));
        let newValue = parseFloat(value?.toFixed(4));

        // Define colors: Green (low), Yellow (mid), Red (high)
        const green = "#28a745";  // Low values
        const yellow = "#ffc107"; // Mid-range values
        const red = "#dc3545";    // High values

        if (newValue > maxVal || newValue < minVal) {
            return "#fff" // if value is not within the max and min then return WHITE color.
        }

        let t = 0; // Default value

        if (maxValue !== minValue) {
            t = (newValue - minValue) / (maxValue - minValue);
        }
        t = Math.min(1, Math.max(0, parseFloat(t.toFixed(4))));

        // Choose color gradient based on 't' value
        let color1, color2;
        if (t <= 0.5) {
            // Low to Mid (Green to Yellow)
            color1 = green;
            color2 = yellow;
            t = t * 2; // Normalize t to range [0,1] within this segment
        } else {
            // Mid to High (Yellow to Red)
            color1 = yellow;
            color2 = red;
            t = (t - 0.5) * 2; // Normalize t to range [0,1] within this segment
        }

        const hexToRgb = (hex) => {
            const bigint = parseInt(hex.slice(1), 16);
            return {
                r: (bigint >> 16) & 255,
                g: (bigint >> 8) & 255,
                b: bigint & 255,
            };
        };

        const rgbToHex = ({ r, g, b }) =>
            `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase()}`;

        const rgb1 = hexToRgb(color1);
        const rgb2 = hexToRgb(color2);

        const interpolatedRgb = {
            r: Math.round(rgb1.r + t * (rgb2.r - rgb1.r)),
            g: Math.round(rgb1.g + t * (rgb2.g - rgb1.g)),
            b: Math.round(rgb1.b + t * (rgb2.b - rgb1.b)),
        };

        return rgbToHex(interpolatedRgb);
    }

    const getTimeFunction = async (cell) => {
        let result = []
        await getCellEvent({ filterdata, packType, cell })
            .then(res => result = res)
            .catch(err => result = [])
        return result;
    }

    async function setSelectedCellData(cellNumber) {
        let obj = { cellNum: cellNumber }
        let key = "";
        let dateAndTime = ""

        if (batteryFilterType === "Cell_Out_Temp_Max") {
            key = `Cell_${cellNumber}_Out_Temp_Max`;
            obj.Out_Temp = cellData[key] ?? null;
        }
        else if (batteryFilterType === "Cell_Out_Temp_Min") {
            key = `Cell_${cellNumber}_Out_Temp_Min`;
            obj.Out_Temp = cellData[key] ?? null;
        }
        else if (batteryFilterType === "overall_cell_data") {
            key = `Cell_${cellNumber}_Out_Temp_Last_Point`;
            obj.Out_Temp = overAllData[key] ?? null;
        }
        else if (batteryFilterType === "Cell_Out_Volt_Min") {
            key = `Cell_${cellNumber}_Out_Volt_Min`;
            obj.Out_Temp = cellData[key] ?? null;
        }
        else if (batteryFilterType === "Cell_Out_Volt_Max") {
            key = `Cell_${cellNumber}_Out_Volt_Max`;
            obj.Out_Temp = cellData[key] ?? null;
        }
        else if (batteryFilterType === "Thermal_runaway") {
            key = `Cell_${cellNumber}_Thermal_Runaway`;
            obj.Out_Temp = cellData[key] ?? null;
            obj.events = await getTimeFunction(cellNumber)
        }

        setSelectedCell(obj)
    }

    const setDetailsFunction = (cellNumber) => {

        let type = batteryFilterType.includes("Max") ? "max" : "min"
        let bodyObj = { filterdata, packType, cellNo: cellNumber, type: type }
        let apiType = batteryFilterType.includes("Temp") ? "Temperature" : "Voltage"

        getTemperatureVoltageByCell(bodyObj, apiType)
            .then(res => {
                setCommentDetails({
                    value: res?.temperature || res?.voltage || 0,
                    date: res?.eventdate,
                })
            }
            ).catch(err => setCommentDetails({ value: null, date: null }))
    }

    function setColorAsPerValue(cellNumber) {
        let value = 0
        if (batteryFilterType == "Cell_SOH_BasedOn_Capacity_Min") {
            value = cellData[`Cell_${cellNumber}_SOH_BasedOn_Capacity_Min`]
        }
        if (batteryFilterType == "Cell_IR_ohmic_Max") {
            value = cellData[`Cell_${cellNumber}_IR_ohmic_Max`]
        }
        if (batteryFilterType == "cell_RUL") {
            value = cellData[`Cell_${cellNumber}_RUL`]
        }
        if (batteryFilterType == "Cell_Out_Temp_Min") {
            value = cellData[`Cell_${cellNumber}_Out_Temp_Min`]

        }
        if (batteryFilterType == "Cell_Out_Temp_Max") {
            value = cellData[`Cell_${cellNumber}_Out_Temp_Max`]
        }
        if (batteryFilterType == "overall_cell_data") {
            value = cellData[`Cell_${cellNumber}_Out_Temp_Last_Point`]
        }
        if (batteryFilterType == "Cell_Out_Volt_Min") {
            value = cellData[`Cell_${cellNumber}_Out_Volt_Min`]
        }
        if (batteryFilterType == "Cell_Out_Volt_Max") {
            value = cellData[`Cell_${cellNumber}_Out_Volt_Max`]
        }
        if (batteryFilterType == "Thermal_runaway") {
            value = cellData[`Cell_${cellNumber}_Thermal_Runaway`]
        }
        return (value)
    }

    const renderTooltip = (props, cellNumber) => (
        <Tooltip id="button-tooltip" {...props}>
            <div className='bdt_tooltip'>
                {
                    batteryFilterType === "Cell_Out_Temp_Max" &&
                    <div>Temperature (°C): {
                        cellData[`Cell_${cellNumber}_Out_Temp_Max`] != null
                            ? cellData[`Cell_${cellNumber}_Out_Temp_Max`].toFixed(2)
                            : "No Data"
                    }
                    </div>
                }

                {
                    batteryFilterType === "Cell_Out_Temp_Min" &&
                    <div>Temperature (°C):
                        {
                            cellData[`Cell_${cellNumber}_Out_Temp_Min`] != null
                                ? cellData[`Cell_${cellNumber}_Out_Temp_Min`].toFixed(2)
                                : "No Data"
                        }
                    </div>
                }
                {
                    batteryFilterType === "Cell_Out_Volt_Min" &&
                    <div>Voltage:
                        {
                            cellData[`Cell_${cellNumber}_Out_Volt_Min`] != null
                                ? cellData[`Cell_${cellNumber}_Out_Volt_Min`].toFixed(2) + " V"
                                : "No Data"
                        }
                    </div>
                }
                {
                    batteryFilterType === "Cell_Out_Volt_Max" &&
                    <div>Voltage:
                        {
                            cellData[`Cell_${cellNumber}_Out_Volt_Max`] != null
                                ? cellData[`Cell_${cellNumber}_Out_Volt_Max`].toFixed(2) + " V"
                                : "No Data"
                        }
                    </div>
                }
                {
                    batteryFilterType === "Thermal_runaway" &&
                    <div>
                        {
                            cellData[`Cell_${cellNumber}_Thermal_Runaway`] != null
                                ? cellData[`Cell_${cellNumber}_Thermal_Runaway`].toFixed(2)
                                : "No Data"
                        }
                    </div>
                }
                {
                    batteryFilterType === "overall_cell_data" &&
                    <div>Temperature (°C):
                        {
                            overAllData[`Cell_${cellNumber}_Out_Temp_Last_Point`] != null
                                ? overAllData[`Cell_${cellNumber}_Out_Temp_Last_Point`].toFixed(2)
                                : "No Data"
                        }
                    </div>
                }

            </div>
        </Tooltip>
    );

    const noteGenerator = (value) => {
        return value == 0 ? "Normal/No Risk.​" : value == 1 ? "Warning/Medium Risk.​" : "Critical/High Risk.​"
    }

    const commentGenerator = (batteryFilterType) => {
        return (
            ["Cell_Out_Temp_Max", "Cell_Out_Temp_Min", "Cell_Out_Volt_Max", "Cell_Out_Volt_Min"].includes(batteryFilterType) ?
                <li>
                    {
                        batteryFilterType?.includes("Cell_Out_Temp_Max") ?
                            <b>Max cell temp: {commentDetails?.value}°C cell temperature was recorded in the selected date range on date {commentDetails?.date}.</b>
                            : batteryFilterType?.includes("Cell_Out_Temp_Min") ?
                                <b>Min cell temp: {commentDetails?.value}°C cell temperature was recorded in the selected date range on date {commentDetails?.date}.</b>
                                : batteryFilterType?.includes("Cell_Out_Volt_Max") ?
                                    <b>Max cell voltage: {commentDetails?.value}V cell voltage was recorded in the selected date range on date {commentDetails?.date}.</b>
                                    : batteryFilterType?.includes("Cell_Out_Volt_Min") ?
                                        <b>Min cell voltage: {commentDetails?.value}V cell voltage was recorded in the selected date range on date {commentDetails?.date}.</b>
                                        : ""
                    }
                </li>
                : <></>
        )
    }

    const textColorFunction=(cellNo)=>{
        let value = setColorAsPerValue(cellNo)
        let presentOutOfRange= value<minVal || value>maxVal || value==null

        let textColor= presentOutOfRange ?"text-dark":"text-light"
        return textColor
    }

    return (
        <>
            <div className="row">
                <div className="col-3 align-content-center text-center">
                    <select className="form-select form-select-md" aria-label="Default select example" value={batteryFilterType} onChange={(e) => setBatteryFilterType(e.target.value)} >
                        <option value="Cell_Out_Temp_Max">Cell Max Temp</option>
                        <option value="Cell_Out_Temp_Min">Cell Min Temp</option>
                        <option value="Cell_Out_Volt_Min">Cell_Out_Volt_Min</option>
                        <option value="Cell_Out_Volt_Max">Cell_Out_Volt_Max</option>
                        <option value="Thermal_runaway">Thermal RunAway</option>
                    </select>
                </div>
                <div className="col-9 d-flex align-content-center text-center justify-content-center xxx">
                    {Object.keys(cellData).length && isFinite(maxVal) ? <ColorLegend
                        colorRange={["#28a745", "#ffc107", "#dc3545"]}  // Green → Yellow → Red
                        height={35}
                        width={600}
                        max={maxVal}
                        min={minVal}
                    /> : <></>}
                </div>
            </div>

            <div className="container my-4 battery-container">
                <div className="row">
                    {/* First set of columns */}
                    <div className="col-lg-4 col-md-6">
                        <h5 className="text-center module-heading py-1" >Module 2</h5>
                        {
                            loaderForCells ? <div style={{ height: "500px" }}><ChartLoader zIndex={1} /></div> :
                                <div className="d-flex flex-wrap justify-content-center">
                                    {
                                        Object.keys(cellData).length ?
                                            columns.map((col, colIndex) => (
                                                <div className="d-flex flex-column w-25" key={colIndex}>
                                                    {col.map(({ cellNumber, cellTemp }, index) => (
                                                        <OverlayTrigger
                                                            key={index}
                                                            placement="right"
                                                            delay={{ show: 250, hide: 400 }}
                                                            overlay={(props) => renderTooltip(props, cellNumber + 104)}
                                                        >

                                                            <div
                                                                onClick={() => (setSelectedCellData(cellNumber + 104), setDetailsFunction(cellNumber + 104))}
                                                                key={cellNumber}
                                                                className={`border text-center cell curser-pointer  ${textColorFunction(cellNumber + 104)}`}
                                                                data-bs-toggle="tooltip" data-bs-placement="right"
                                                                style={{ backgroundColor: `${interpolateColorBasedOnValue(setColorAsPerValue(cellNumber + 104))}`}}
                                                            >
                                                                {cellNumber + 104}
                                                            </div>
                                                        </OverlayTrigger>
                                                    ))}
                                                </div>
                                            )) : <>No Data Available</>
                                    }
                                </div>
                        }
                    </div>

                    {/* Second set of columns */}
                    <div className="col-lg-4 col-md-6">
                        <h5 className="text-center module-heading py-1">Module 1</h5>
                        {
                            loaderForCells ? <div style={{ height: "500px" }}><ChartLoader zIndex={1} /></div> :
                                <div className="d-flex flex-wrap justify-content-center">
                                    {
                                        Object.keys(cellData).length ?
                                            columns.map((col, colIndex) => (
                                                <div className="d-flex flex-column w-25" key={colIndex}>
                                                    {col.map(({ cellNumber, cellTemp }, index) => (
                                                        <OverlayTrigger
                                                            key={index}
                                                            placement="right"
                                                            delay={{ show: 250, hide: 400 }}
                                                            overlay={(props) => renderTooltip(props, cellNumber)}
                                                        >
                                                            <div
                                                                onClick={() => (setSelectedCellData(cellNumber), setDetailsFunction(cellNumber))}
                                                                key={cellNumber}
                                                                className={`border text-center cell curser-pointer  ${textColorFunction(cellNumber)}`}
                                                                data-bs-toggle="tooltip" data-bs-placement="right"
                                                                style={{ backgroundColor: `${interpolateColorBasedOnValue(setColorAsPerValue(cellNumber))}` }}
                                                            >
                                                                {cellNumber}
                                                            </div>
                                                        </OverlayTrigger>
                                                    ))}
                                                </div>
                                            )) : <>No Data Available</>
                                    }
                                </div>
                        }
                    </div>
                    <div className="col-lg-4 col-md-12 d-flex flex-column">
                        <h5 className="text-center module-heading py-1">Preview</h5>
                        {
                            loaderForCells ? <div style={{ height: "500px" }}><ChartLoader zIndex={1} /></div> :
                                <div className="flex-grow-1 display-info-div" style={{ backgroundColor: `${Object.keys(cellData).length ? interpolateColorBasedOnValue(setColorAsPerValue(selectedCell.cellNum)) : ""}` }}>
                                    {
                                        (Object.keys(cellData).length && selectedCell.cellNum > 0) ?
                                            <div className={`text-start h-75 ${textColorFunction(selectedCell.cellNum)}`}>
                                                <ul>
                                                    <li> <b>Cell Id: {selectedCell.cellNum} </b></li>
                                                    {selectedCell.Out_Temp != null ? (
                                                        <>
                                                            <li >
                                                                {
                                                                    batteryFilterType?.includes("Volt") ?
                                                                        <b>Voltage: {selectedCell.Out_Temp?.toFixed(2)} V</b>
                                                                        : batteryFilterType?.includes("Temp")
                                                                            ? <b>Temperature (°C): {selectedCell.Out_Temp?.toFixed(2)}°C</b>
                                                                            : batteryFilterType.includes("Thermal_runaway") ? <>
                                                                                <b>Note: {noteGenerator(selectedCell?.Out_Temp?.toFixed(2))} </b>
                                                                                <br />
                                                                                <li className='fw-bolder'>Occurred At:
                                                                                    <ul className='scroll-container-BDT'>
                                                                                        {selectedCell?.events?.reverse()?.map((item) =>
                                                                                            <>
                                                                                                <li>
                                                                                                    <small className='fw-bold'>{item.event_type}</small>
                                                                                                    <small className='ml-1'>{moment(item?.created_timestamp, "YYYY-MM-DD HH:mm:ss").format("DD MMM YYYY hh:mm:ss A")}.</small>
                                                                                                </li>
                                                                                            </>
                                                                                        )
                                                                                        }
                                                                                    </ul>
                                                                                </li>

                                                                            </>
                                                                                : ""}
                                                            </li>
                                                            {commentGenerator(batteryFilterType)}
                                                        </>
                                                    ) : (
                                                        <li>
                                                            No Data
                                                        </li>
                                                    )}
                                                </ul>
                                            </div>
                                            : <>Preview here</>
                                    }
                                </div>
                        }
                    </div>
                </div>
            </div>
        </>
    );
};

export default BDT_Battery;
